<template>
  <NewBsBox :bs="{ 'cmp.multicascade': 'wrapper' }">
    <NewBsBox :bs="{ 'cmp.multicascade': 'header' }">
      {{ numberCn }}级{{ optionName }}
    </NewBsBox>

    <NewBsBox
      v-if="onSearch"
      :bs="{ py: 6, px: 12 }"
    >
      <BeerInput
        v-model="keyword"
        :clearable="true"
        :input-on="inputOn"
        :bs="{ width: '100%' }"
        @on-clear="clearSearch"
      >
        <template #left>
          <BeerIcon
            :bs="{
              ml: 11, fill: 'mute'
            }"
            icon="search"
          />
        </template>
      </BeerInput>
    </NewBsBox>

    <NewBsBox :bs="{ 'cmp.multicascade': 'levelbox' }">
      <template
        v-if="prevContext"
      >
        <NewBsBox
          v-if="!prevContext.loaded || searching"
          :bs="{ 'cmp.multicascade': 'empty'}"
        >
          <BeerWidgetLoading />
        </NewBsBox>
        <NewBsBox
          v-else-if="prevContext.error"
          :bs="{ 'cmp.multicascade': 'empty'}"
        >
          <div>{{ prevContext.error }}</div>
          <BeerIcon
            v-if="prevContext.retry"
            :size="14"
            :bs="{ ml: 8, cursor: 'pointer' }"
            :grid-size="36"
            :icon-path="$options.RETRY_PATH"
            color="danger"
            @click.stop="prevContext.retry"
          />
        </NewBsBox>
        <NewBsBox
          v-else-if="searchError"
          :bs="{ 'cmp.multicascade': 'empty'}"
        >
          <div>{{ searchError }}</div>
        </NewBsBox>

        <NewBsBox
          v-else-if="optionList.length < 1"
          :bs="{ 'cmp.multicascade': 'empty'}"
        >
          {{ searched ? '无匹配结果' : '无内容' }}
        </NewBsBox>

        <template v-else>
          <CheckOption
            v-if="showAll"
            :checked="checkedHalf || allChecked"
            :on-change="checkAll"
            :indeterminate="checkedHalf"
            label="全选"
          />
          <template v-for="c of optionList">
            <CheckNavOption
              v-if="c.hasChildren"
              :key="c.value[identKey]"
              :label="c.value[textKey]"
              :checked="c.checked"
              :disabled="c.disabled"
              :handle-change="() => onChangeNavCheck(c)"
              :indeterminate="c.indeterminate"
              :data-active="c.active"
              @click="() => onClick(c.value)"
            />
            <CheckOption
              v-else
              :key="c.value[identKey]"
              :disabled="c.disabled"
              :label="c.value[textKey]"
              :checked="c.checked"
              :on-change="() => onCheckNormal(c, { click: true })"
            />
          </template>
        </template>
      </template>

      <NewBsBox
        v-else
        :bs="{ 'cmp.multicascade': 'empty', fontSize: '14px'}"
      >
        请选择上一级{{ optionName }}
      </NewBsBox>

    </NewBsBox>
  </NewBsBox>
</template>

<script>
  import { isNil } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerWidgetLoading from '@xhs/yam-beer/cmp/common/Spinner/WidgetLoading.vue'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import BeerInput from '@xhs/yam-beer/cmp/dataEntry/Input/Input.vue'
  import {
    RETRY_PATH,
  } from '@xhs/yam-beer/cmp/constants/builtinIcon'
  import CheckNavOption from '@xhs/yam-beer/cmp/overlayers/Option/CheckNavOption.vue'
  import CheckOption from '@xhs/yam-beer/cmp/overlayers/Option/CheckOption.vue'
  import { getChildrenForItem } from '@xhs/yam-beer/utils/cascadeUtils'
  import initStyle from './style'

  initStyle()

  const transLevelDict = {
    1: '一',
    2: '二',
    3: '三',
    4: '四',
    5: '五',
    6: '六',
    7: '七',
    8: '八',
    9: '九',
    10: '十',
  }

  export default {
    name: 'MultiCascadeLevel',
    components: {
      NewBsBox,
      BeerIcon,
      BeerInput,
      BeerWidgetLoading,
      CheckNavOption,
      CheckOption,
    },
    RETRY_PATH,
    props: {
      // === props allowed to be cutomization ===
      /**
       * 是否禁止全选、反选功能
       */
      disableSelectAll: {
        type: Boolean,
        default: true,
      },
      /**
       * 带箭头的选项是否能选择
       */
      navSelectable: {
        type: Boolean,
        default: false,
      },
      /**
       * 判断选项是否带有字节点
       */
      hasChildren: {
        type: Function,
        default: item => item.hasChildren,
      },
      /**
       * 选项名称，显示在标题或提示
       */
      optionName: {
        type: String,
        default: '选项',
      },
      /**
       * 选项搜索功能, { level, keyword, context }
       */
      onSearch: {
        type: Function,
      },
      /**
       * 解析 value 显示中文名称的字段
       */
      textKey: {
        type: String,
        default: 'name',
      },
      // === internal props, meant to be used with Tree.vue ===
      identKey: {
        type: String,
        default: 'id',
      },
      /**
       * 预加载遍历选项树状结构
       */
      fetchItem: {
        type: Function,
      },
      /**
       * 内部级联缓存数据，方便索引或写入树状结构数据
       */
      childrenState: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 选项列表所属的 level
       */
      level: {
        type: Number,
        default: 0,
      },
      value: {
        type: Array,
        default: () => ([]),
      },
      /**
       * 高亮的选项 id
       */
      activeId: {
        type: [String, Number],
      },
      /**
       * 选项值的 key
       */
      valueKey: {
        type: String,
        default: 'id',
      },
      prevContext: {
        type: Object,
        defalut: () => ({}),
      },
      /**
       * 当点击选项名称
       */
      onView: {
        type: Function,
      },
      /**
       * 当点击选项 checkbox
       */
      onCheckItem: {
        type: Function,
        default: () => ({}),
      },
      setChildren: {
        type: Function,
      },
      showAllCheck: {
        type: Function,
      },
    },
    data() {
      return {
        keyword: null,
        searchedList: [],
        searched: false,
        searching: false,
        searchError: null,
      }
    },
    computed: {
      numberCn() {
        return transLevelDict[this.level]
      },
      inputOn() {
        return {
          keyup: e => {
            if (e.keyCode === 13) {
              this.handleSearch()
            }
          },
        }
      },
      isParentChecked() {
        return this.prevContext.parentChecked
      },
      showAll() {
        let isOn = true
        if (this.showAllCheck) {
          isOn = this.showAllCheck(this.level)
        }
        if (!isOn) {
          return false
        }
        return this.optionList.length > 0
          && this.optionList.every(o => !o.hasChildren)
      },
      allChecked() {
        return this.optionList.every(o => o.checked)
      },
      checkedHalf() {
        return this.optionList.some(o => o.checked)
          && !this.allChecked
      },
      optionList() {
        if (!this.prevContext.children) {
          return []
        }
        const lst = this.searched
          ? this.searchedList
          : this.prevContext.children
        return lst
          .map(item => {
            const value = item
            const isParentChecked = this.isParentChecked && this.navSelectable
            if (!this.hasChildren(item)) {
              return {
                hasChildren: false,
                value,
                checked: isParentChecked || this.isSelected(item),
                disabled: isParentChecked,
              }
            }
            const checked = isParentChecked || this.checkedNav(item, this.level)
            return {
              value,
              active: (this.activeId && this.activeId === item[this.identKey]) || null,
              hasChildren: true,
              checked,
              disabled: this.disableSelectAll || isParentChecked,
              indeterminate: checked ? this.halfCheckedNav(item, this.level) : false,
            }
          })
      },
    },
    methods: {
      checkAll() {
        const allChecked = this.allChecked
        this.optionList.forEach(o => {
          if (allChecked) {
            this.onCheckNormal(o, { click: false })
          } else if (!o.checked) {
            this.onCheckNormal(o, { click: false })
          }
        })
      },
      clearSearch() {
        this.handleSearch(true)
      },
      handleSearch(clear) {
        this.onClick(null)
        if (!this.keyword || clear) {
          this.searched = false
          this.searching = false
          this.searchError = null
          this.searchedList = []
        } else {
          this.searched = true
          this.searching = true
          this.onSearch({
            level: this.level,
            keyword: this.keyword,
            context: this.prevContext,
          })
            .then((lst = []) => {
              this.searching = false
              this.searchedList = lst
              if (lst.length > 0 && this.setChildren) {
                this.setChildren(`searched-${this.level}-${this.keyword}`, {
                  children: lst,
                })
              }
            })
            .catch(e => {
              this.searching = false
              this.searchError = e.message ? e.message : '搜索失败'
            })
        }
      },
      onCheckNormal(computedItem, { click = true }) {
        if (this.navSelectable && this.isParentChecked) {
          return
        }
        if (click) {
          this.onClick(computedItem.value)
        }
        this.onCheckItem(computedItem.value, computedItem.checked)
      },
      onChangeNavCheck(computedItem) {
        if (this.disableSelectAll || this.isParentChecked) {
          return
        }
        if (this.navSelectable) {
          this.onCheckItem(computedItem.value, computedItem.checked)
          return
        }
        if (!computedItem.checked || computedItem.indeterminate) {
          // check all if none checked or partial cheched
          this.iterateCheckItem(computedItem.value, this.level, false)
        } else {
          // uncheck all if all checked
          this.iterateCheckItem(computedItem.value, this.level, true)
        }
      },
      iterateCheckItem(item, level, isRemove) {
        if (this.hasChildren(item)) {
          const children = getChildrenForItem(
            this.childrenState, level, item, this.identKey,
          )
          children.forEach(c => this.iterateCheckItem(c, level + 1, isRemove))
        } else {
          const isSelected = this.isSelected(item)
          if (isSelected && isRemove) {
            this.onCheckItem(item, isRemove)
          }
          if (!isSelected && !isRemove) {
            this.onCheckItem(item, isRemove)
          }
        }
      },
      checkedNav(item, level) {
        if (this.navSelectable) {
          return this.isSelected(item)
        }
        const children = getChildrenForItem(
          this.childrenState, level, item, this.identKey,
        )
        return children.some(c => {
          if (this.hasChildren(c)) {
            return this.checkedNav(c, level + 1)
          }
          return this.value.indexOf(c[this.valueKey]) > -1
        })
      },
      halfCheckedNav(item, level) {
        if (this.navSelectable) {
          return false
        }
        const children = getChildrenForItem(
          this.childrenState, level, item, this.identKey,
        )
        const allChecked = children.every(c => {
          if (this.hasChildren(c)) {
            return this.checkedNav(c, level + 1) && !this.halfCheckedNav(c, level + 1)
          }
          return this.value.indexOf(c[this.valueKey]) > -1
        })
        return !allChecked
      },
      isSelected(item) {
        if (isNil(item[this.valueKey])) {
          return false
        }
        return this.value.indexOf(item[this.valueKey]) > -1
      },
      onClick(item) {
        if (this.onView) {
          this.onView(item)
        }
      },
    },
  }
</script>
